import React, { useEffect, useState, useCallback } from 'react';
import { Check, Link2, AlertCircle, Landmark, X, Split } from 'lucide-react';
import api from '../../api/client';
import InFinestra from '../../components/InFinestra';

// Bonifici della banca proposti ai corsisti (16/10/2026, titolare): mai assegnati in automatico.
// "Collega" crea il pagamento del corsista legato al movimento della banca (data e importo del bonifico);
// "Aggancia" collega un pagamento già scritto a mano, così non nasce un doppione.
const eur = (n) => `${(+n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const gg = (d) => d ? d.slice(0, 10).split('-').reverse().join('/') : '—';
const LIVELLO = {
  ALTA:  { label:'Quasi sicuro', color:'#15803D', bg:'#22C55E1F' },
  MEDIA: { label:'Probabile',    color:'#B45309', bg:'#F59E0B1F' },
  BASSA: { label:'Da verificare',color:'#6B7280', bg:'#9CA3AF22' },
};

export const Livello = ({ l }) => {
  const x = LIVELLO[l] || LIVELLO.BASSA;
  return <span style={{ fontSize:'var(--font-size-xs)', fontWeight:700, padding:'2px 8px', borderRadius:'99px', background:x.bg, color:x.color, whiteSpace:'nowrap' }}>{x.label}</span>;
};

const Finestra = ({ f, ultimo }) => (
  <div style={s.info}>
    Bonifici in entrata dal <b>{gg(f.da)}</b>{f.a ? <> al <b>{gg(f.a)}</b></> : ''}
    {f.stimata && <> (date del ciclo non indicate: metti "Apertura iscrizioni" e "Fine corsi" nel ciclo, in Planning corsi, per cercare nel periodo giusto)</>}.
    {ultimo && <> Movimenti della banca caricati fino al <b>{gg(ultimo)}</b>: per i bonifici più recenti carica l'estratto conto aggiornato in Movimenti bancari.</>}
    {' '}Ci sono anche i movimenti della cassa Contanti (💶): collegandoli nasce un pagamento in contanti. Tessere FIPAV, affitti campo, tornei, Satispay e sponsor non sono considerati.
  </div>
);

// La X: "non è suo", il bonifico non viene più proposto a questo corsista (si rimette con "Rimetti")
function Scarta({ atletaSlotId, bonificoId, puoCollegare, onFatto }) {
  const [lavoro, setLavoro] = useState(false);
  if (!puoCollegare) return null;
  const scarta = async (e) => {
    e.stopPropagation();
    setLavoro(true);
    try { await api.post(`/planning/atletaslot/${atletaSlotId}/scarta-bonifico`, { bank_transaction_id: bonificoId }); onFatto(); }
    catch (err) { alert(err.response?.data?.error || 'Errore'); setLavoro(false); }
  };
  return <button style={s.btnX} disabled={lavoro} onClick={scarta} title="Non è suo: non proporlo più a questa persona"><X size={14}/></button>;
}

// Riga d'azione: importo modificabile (un bonifico può pagare più persone) + Collega / Aggancia
// Contanti già in cassa per la stessa persona e lo stesso importo: avviso prima di collegare
const Doppione = ({ d }) => d ? (
  <div style={{ fontSize:'var(--font-size-xs)', color:'#B91C1C', background:'#EF44441A', borderRadius:'6px', padding:'6px 8px', margin:'4px 0', display:'flex', gap:'6px', alignItems:'flex-start' }}>
    <AlertCircle size={13} style={{ flexShrink:0, marginTop:'1px' }}/>
    <span>Possibile doppione in cassa: c'è già un pagamento in contanti uguale del {gg(d.data)}, già registrato nella cassa Contanti. Se sono gli stessi soldi, non collegare e togli questo movimento da Movimenti bancari → Contanti.</span>
  </div>
) : null;

function Azioni({ atletaSlotId, bonifico, saldo, pagamentoSimile, puoCollegare, onFatto }) {
  const proposto = saldo > 0 && bonifico.disponibile > saldo + 0.005 ? saldo : bonifico.disponibile;
  const [importo, setImporto] = useState(String(proposto));
  const [lavoro, setLavoro] = useState(false);
  if (!puoCollegare) return null;
  const invia = async (body) => {
    setLavoro(true);
    try { await api.post(`/planning/atletaslot/${atletaSlotId}/collega-bonifico`, { bank_transaction_id: bonifico.id, ...body }); onFatto(); }
    catch (e) { alert(e.response?.data?.error || 'Errore nel collegamento'); }
    finally { setLavoro(false); }
  };
  return (
    <div style={{ display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap' }} onClick={e => e.stopPropagation()}>
      {pagamentoSimile ? (
        <button style={s.btnPri} disabled={lavoro} title={`C'è già un pagamento ${bonifico.contanti ? 'in contanti' : 'a bonifico'} con questo importo scritto a mano: lo collego a questo movimento, senza crearne un altro`}
          onClick={() => invia({ pagamento_id: pagamentoSimile })}><Link2 size={12}/> Aggancia al pagamento già scritto</button>
      ) : (<>
        <input style={{ ...s.inp, width:'96px' }} type="number" step="0.01" min="0" value={importo} onChange={e => setImporto(e.target.value)} title="Quanto di questo bonifico va a questa persona"/>
        <button style={s.btnPri} disabled={lavoro || !(parseFloat(importo) > 0)}
          onClick={() => invia({ importo })}><Check size={12}/> Collega</button>
      </>)}
    </div>
  );
}

const Bonifico = ({ b, children }) => (
  <div style={{ minWidth:0 }}>
    <div style={{ display:'flex', alignItems:'baseline', gap:'8px', flexWrap:'wrap' }}>
      <span style={{ fontFamily:"'Barlow Condensed'", fontSize:'var(--font-size-lg)', fontWeight:700, color:'#16A34A' }}>{eur(b.importo)}</span>
      {b.disponibile < b.importo - 0.005 && <span style={{ fontSize:'var(--font-size-xs)', color:'#B45309', fontWeight:600 }}>restano {eur(b.disponibile)} da assegnare</span>}
      <span style={{ fontSize:'var(--font-size-xs)', color:'var(--text-muted)' }}>{gg(b.data)}</span>
      {b.contanti
        ? <span title="Movimento della cassa Contanti: collegandolo nasce un pagamento in contanti" style={{ fontSize:'var(--font-size-xs)', fontWeight:700, padding:'2px 8px', borderRadius:'99px', background:'#F59E0B1F', color:'#B45309' }}>💶 Contanti</span>
        : <span style={{ fontSize:'var(--font-size-xs)', fontWeight:700, padding:'2px 8px', borderRadius:'99px', background:'#3B82F61A', color:'#1D4ED8' }}>🏦 Bonifico</span>}
      {children}
    </div>
    <div style={{ fontSize:'var(--font-size-sm)', marginTop:'2px', wordBreak:'break-word' }}>{b.causale || '(senza causale)'}</div>
    <div style={{ fontSize:'var(--font-size-xs)', color:'var(--text-muted)' }}>{b.contanti ? 'dato da' : 'da'} {b.ordinante || '—'}{b.categoria ? ` · ${b.categoria}` : ''}</div>
  </div>
);

// "Dividi tra più persone" (23/10/2026, titolare): un bonifico unico che paga più corsisti (genitore per due
// figli, amici con cognomi diversi). Si parte dai corsisti proposti, se ne aggiungono altri cercando il nome,
// ognuno con il suo importo; una conferma crea tutti i pagamenti insieme. Il resto rimane da assegnare.
const cent = (n) => Math.round(n * 100) / 100;
function Dividi({ b, corsisti, cicloId, onChiudi, onFatto }) {
  const [righe, setRighe] = useState(() => {
    let resto = b.disponibile;
    return b.candidati.slice(0, 2).map(c => {
      const imp = cent(Math.max(0, c.saldo > 0 ? Math.min(c.saldo, resto) : resto / 2));
      resto = cent(resto - imp);
      return { ...c, importo: String(imp) };
    });
  });
  const [cerca, setCerca] = useState('');
  const [lavoro, setLavoro] = useState(false);
  const [errore, setErrore] = useState('');
  const totale = cent(righe.reduce((t, r) => t + (parseFloat(r.importo) || 0), 0));
  const resto = cent(b.disponibile - totale);
  const scelti = new Set(righe.map(r => r.atleta_slot_id));
  const q = cerca.trim().toLowerCase();
  const trovati = q.length < 2 ? [] : corsisti.filter(c => !scelti.has(c.atleta_slot_id) && c.nome.toLowerCase().includes(q)).slice(0, 8);
  const aggiungi = (c) => { setRighe(r => [...r, { ...c, importo: String(Math.max(0, c.saldo > 0 ? Math.min(c.saldo, resto) : resto)) }]); setCerca(''); };
  const valido = righe.length >= 2 && righe.every(r => parseFloat(r.importo) > 0) && resto >= -0.005;
  const conferma = async () => {
    setLavoro(true); setErrore('');
    try {
      await api.post(`/planning/bonifici-corsi/${b.id}/dividi`, { ciclo_id: cicloId, righe: righe.map(r => ({ atleta_slot_id: r.atleta_slot_id, importo: r.importo })) });
      onFatto();
    } catch (e) { setErrore(e.response?.data?.error || 'Divisione non riuscita'); setLavoro(false); }
  };
  return (
    <InFinestra attiva onChiudi={onChiudi} larghezza={620}>
      <div style={{ padding:'18px 20px' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'10px' }}>
          <b style={{ fontSize:'var(--font-size-lg)' }}>Dividi tra più persone</b>
          <button style={s.btnX} onClick={onChiudi}><X size={14}/></button>
        </div>
        <div style={{ ...s.info, marginBottom:'14px' }}><Bonifico b={b}/></div>
        <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
          {righe.map((r, i) => (
            <div key={r.atleta_slot_id} style={s.candidato}>
              <div style={{ minWidth:0, flex:1 }}>
                <b>{r.nome}</b>
                <div style={{ fontSize:'var(--font-size-xs)', color: r.saldo > 0 ? '#DC2626' : '#16A34A' }}>{r.saldo > 0 ? `deve ancora ${eur(r.saldo)}` : 'già saldato'}{r.turni ? ` · ${r.turni}` : ''}</div>
              </div>
              <input style={{ ...s.inp, width:'96px' }} type="number" step="0.01" min="0" value={r.importo}
                onChange={e => setRighe(rr => rr.map((x, j) => j === i ? { ...x, importo: e.target.value } : x))} title="Quanto di questo bonifico va a questa persona"/>
              <button style={s.btnX} onClick={() => setRighe(rr => rr.filter((_, j) => j !== i))} title="Togli dalla divisione"><X size={14}/></button>
            </div>
          ))}
        </div>
        <div style={{ position:'relative', marginTop:'10px' }}>
          <input style={{ ...s.inp, width:'100%' }} placeholder="Aggiungi una persona: scrivi nome o cognome…" value={cerca} onChange={e => setCerca(e.target.value)}/>
          {trovati.length > 0 && (
            <div style={s.tendina}>
              {trovati.map(c => (
                <button key={c.atleta_slot_id} style={s.voce} onClick={() => aggiungi(c)}>
                  <b>{c.nome}</b> <span style={{ color:'var(--text-muted)', fontSize:'var(--font-size-xs)' }}>{c.saldo > 0 ? `deve ${eur(c.saldo)}` : 'saldato'}{c.turni ? ` · ${c.turni}` : ''}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div style={{ marginTop:'14px', fontSize:'var(--font-size-sm)', fontWeight:600, color: resto < -0.005 ? '#DC2626' : 'var(--text)' }}>
          Assegnati {eur(totale)} di {eur(b.disponibile)}
          {resto > 0.005 && <span style={{ color:'#B45309' }}> · restano {eur(resto)} da assegnare</span>}
          {resto < -0.005 && <> · hai superato il bonifico di {eur(-resto)}</>}
        </div>
        {righe.length < 2 && <div style={{ fontSize:'var(--font-size-xs)', color:'var(--text-muted)', marginTop:'4px' }}>Servono almeno due persone (per una sola usa "Collega").</div>}
        {errore && <div style={{ display:'flex', alignItems:'center', gap:'6px', color:'#DC2626', fontSize:'var(--font-size-sm)', marginTop:'8px' }}><AlertCircle size={14}/> {errore}</div>}
        <div style={{ display:'flex', justifyContent:'flex-end', gap:'8px', marginTop:'14px' }}>
          <button style={s.pill} onClick={onChiudi}>Annulla</button>
          <button style={{ ...s.btnPri, opacity: valido ? 1 : .5 }} disabled={!valido || lavoro} onClick={conferma}>
            <Check size={12}/> Conferma: {righe.length} pagamenti, {eur(totale)}
          </button>
        </div>
      </div>
    </InFinestra>
  );
}

// Dentro la riga del corsista (Pagamento corsi): i bonifici che probabilmente sono suoi
export function BonificiProposti({ corsista, puoCollegare, onCollegato }) {
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState('');
  const carica = useCallback(() => {
    api.get(`/planning/atletaslot/${corsista.id}/bonifici-proposti`)
      .then(r => setDati(r.data)).catch(e => setErrore(e.response?.data?.error || 'Errore'));
  }, [corsista.id]);
  useEffect(() => { carica(); }, [carica]);
  if (errore) return null;
  if (!dati) return <div style={{ fontSize:'var(--font-size-xs)', color:'var(--text-muted)', marginTop:'10px' }}>Cerco i bonifici…</div>;
  return (
    <div style={{ marginTop:'12px' }}>
      <div style={{ fontSize:'var(--font-size-xs)', fontWeight:700, letterSpacing:'2px', color:'var(--text-muted)', marginBottom:'6px', display:'flex', alignItems:'center', gap:'6px' }}>
        <Landmark size={12}/> BONIFICI E CONTANTI CHE POTREBBERO ESSERE SUOI
      </div>
      {dati.proposti.length === 0 ? (
        <div style={{ fontSize:'var(--font-size-xs)', color:'var(--text-muted)' }}>Nessun bonifico o movimento in contanti non assegnato gli somiglia.</div>
      ) : dati.proposti.map(b => (
        <div key={b.id} style={s.riga}>
          <div style={{ display:'flex', alignItems:'flex-start', gap:'8px' }}>
            <div style={{ flex:1, minWidth:0 }}><Bonifico b={b}><Livello l={b.livello}/></Bonifico></div>
            <Scarta atletaSlotId={corsista.id} bonificoId={b.id} puoCollegare={puoCollegare} onFatto={() => { carica(); onCollegato(); }}/>
          </div>
          <div style={{ fontSize:'10px', color:'var(--text-muted)' }}>Perché: {b.motivi.join(', ')}</div>
          <Doppione d={b.doppione_cassa}/>
          <Azioni atletaSlotId={corsista.id} bonifico={b} saldo={corsista.saldo} pagamentoSimile={b.pagamento_simile} puoCollegare={puoCollegare}
            onFatto={() => { carica(); onCollegato(); }}/>
        </div>
      ))}
      {dati.nascosti > 0 && puoCollegare && (
        <button style={s.link} onClick={async (e) => {
          e.stopPropagation();
          try { await api.post(`/planning/atletaslot/${corsista.id}/ripristina-bonifici`); carica(); onCollegato(); }
          catch (err) { alert(err.response?.data?.error || 'Errore'); }
        }}>{dati.nascosti === 1 ? '1 bonifico nascosto con la X' : `${dati.nascosti} bonifici nascosti con la X`} · rimetti</button>
      )}
    </div>
  );
}

// Scheda "Bonifici da assegnare": tutti i bonifici "da corso" del periodo non ancora (del tutto) assegnati
export default function BonificiCorsi({ cicloId, puoCollegare, onCollegato }) {
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState('');
  const [filtro, setFiltro] = useState('proposta');
  const [testo, setTesto] = useState('');
  const [dividi, setDividi] = useState(null);
  const carica = useCallback(() => {
    if (!cicloId) return;
    setErrore('');
    api.get('/planning/bonifici-corsi', { params: { ciclo_id: cicloId } })
      .then(r => setDati(r.data)).catch(e => setErrore(e.response?.data?.error || 'Errore nel caricamento'));
  }, [cicloId]);
  useEffect(() => { setDati(null); carica(); }, [carica]);

  if (errore) return <div style={{ display:'flex', alignItems:'center', gap:'6px', color:'#DC2626', padding:'12px' }}><AlertCircle size={14}/> {errore}</div>;
  if (!dati) return <div style={{ color:'var(--text-muted)', padding:'20px' }}>Caricamento…</div>;

  const t = testo.trim().toLowerCase();
  const conProposta = dati.bonifici.filter(b => b.candidati.length);
  const lista = dati.bonifici.filter(b =>
    (filtro === 'tutti' || (filtro === 'proposta' ? b.candidati.length : !b.candidati.length)) &&
    (!t || `${b.causale} ${b.ordinante} ${b.candidati.map(c => c.nome).join(' ')}`.toLowerCase().includes(t)));

  return (
    <div>
      <Finestra f={dati.finestra} ultimo={dati.ultimo_movimento}/>
      <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', marginBottom:'12px', alignItems:'center' }}>
        {[['proposta', `Con un corsista proposto (${conProposta.length})`], ['senza', `Senza proposta (${dati.bonifici.length - conProposta.length})`], ['tutti', `Tutti (${dati.bonifici.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setFiltro(k)} style={{ ...s.pill, ...(filtro === k ? s.pillOn : {}) }}>{l}</button>
        ))}
        <input style={{ ...s.inp, flex:1, minWidth:'180px' }} placeholder="Cerca nome o causale…" value={testo} onChange={e => setTesto(e.target.value)}/>
      </div>
      {lista.length === 0 && <div style={{ color:'var(--text-muted)', padding:'20px' }}>Nessun bonifico da assegnare.</div>}
      <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
        {lista.map(b => (
          <div key={b.id} style={s.card}>
            <div style={{ display:'flex', alignItems:'flex-start', gap:'8px', flexWrap:'wrap' }}>
              <div style={{ flex:1, minWidth:0 }}><Bonifico b={b}/></div>
              <button style={s.btnSec} onClick={() => setDividi(b)} title="Un bonifico unico che paga più corsisti"><Split size={12}/> Dividi tra più persone</button>
            </div>
            {b.candidati.length === 0 ? (
              <div style={{ fontSize:'var(--font-size-xs)', color:'var(--text-muted)', marginTop:'8px' }}>Nessun corsista del ciclo gli somiglia: cerca la persona in Pagamento corsi e registra il pagamento a mano.</div>
            ) : (
              <div style={{ marginTop:'8px', display:'flex', flexDirection:'column', gap:'6px' }}>
                {b.candidati.map(c => (
                  <div key={c.atleta_slot_id} style={s.candidato}>
                    <div style={{ minWidth:0, flex:1 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap' }}>
                        <b>{c.nome}</b><Livello l={c.livello}/>
                        <span style={{ fontSize:'var(--font-size-xs)', color: c.saldo > 0 ? '#DC2626' : '#16A34A' }}>{c.saldo > 0 ? `deve ancora ${eur(c.saldo)}` : 'già saldato'}</span>
                      </div>
                      <div style={{ fontSize:'var(--font-size-xs)', color:'var(--text-muted)' }}>{c.turni || '—'}</div>
                      <div style={{ fontSize:'10px', color:'var(--text-muted)' }}>Perché: {c.motivi.join(', ')}</div>
                      <Doppione d={c.doppione_cassa}/>
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                      <Azioni atletaSlotId={c.atleta_slot_id} bonifico={b} saldo={c.saldo} pagamentoSimile={c.pagamento_simile} puoCollegare={puoCollegare}
                        onFatto={() => { carica(); onCollegato && onCollegato(); }}/>
                      <Scarta atletaSlotId={c.atleta_slot_id} bonificoId={b.id} puoCollegare={puoCollegare} onFatto={() => { carica(); onCollegato && onCollegato(); }}/>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      {dividi && <Dividi b={dividi} corsisti={dati.corsisti || []} cicloId={cicloId} onChiudi={() => setDividi(null)}
        onFatto={() => { setDividi(null); carica(); onCollegato && onCollegato(); }}/>}
    </div>
  );
}

const s = {
  info:     { fontSize:'var(--font-size-xs)', color:'var(--text-muted)', background:'var(--bg-input)', borderRadius:'10px', padding:'10px 12px', marginBottom:'12px', lineHeight:1.5 },
  card:     { background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'12px', padding:'12px 14px' },
  riga:     { background:'var(--bg-card)', borderRadius:'8px', padding:'8px 10px', marginBottom:'6px', display:'flex', flexDirection:'column', gap:'6px' },
  candidato:{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'10px', flexWrap:'wrap', background:'var(--bg-input)', borderRadius:'8px', padding:'8px 10px' },
  inp:      { background:'var(--bg-card)', border:'1px solid var(--border2)', borderRadius:'8px', padding:'7px 10px', color:'var(--text)', fontSize:'var(--font-size-sm)', outline:'none', boxSizing:'border-box' },
  btnPri:   { display:'flex', alignItems:'center', gap:'6px', background:'#F4600C', color:'#fff', border:'none', borderRadius:'8px', padding:'7px 12px', fontSize:'var(--font-size-xs)', fontWeight:700, cursor:'pointer' },
  pill:     { padding:'6px 12px', borderRadius:'99px', fontSize:'var(--font-size-xs)', fontWeight:600, cursor:'pointer', border:'1px solid var(--border2)', background:'var(--bg-input)', color:'var(--text-muted)' },
  btnX:     { display:'flex', alignItems:'center', justifyContent:'center', width:'28px', height:'28px', flexShrink:0, background:'transparent', color:'var(--text-muted)', border:'1px solid var(--border2)', borderRadius:'8px', cursor:'pointer', padding:0 },
  link:     { background:'none', border:'none', padding:'2px 0', color:'var(--text-muted)', fontSize:'var(--font-size-xs)', textDecoration:'underline', cursor:'pointer' },
  btnSec:   { display:'flex', alignItems:'center', gap:'6px', background:'var(--bg-input)', color:'var(--text)', border:'1px solid var(--border2)', borderRadius:'8px', padding:'6px 10px', fontSize:'var(--font-size-xs)', fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' },
  tendina:  { position:'absolute', left:0, right:0, top:'100%', marginTop:'4px', background:'var(--bg-card)', border:'1px solid var(--border2)', borderRadius:'8px', boxShadow:'0 8px 24px rgba(0,0,0,.12)', zIndex:5, maxHeight:'240px', overflowY:'auto' },
  voce:     { display:'block', width:'100%', textAlign:'left', background:'none', border:'none', borderBottom:'1px solid var(--border)', padding:'8px 10px', color:'var(--text)', fontSize:'var(--font-size-sm)', cursor:'pointer' },
  pillOn:   { background:'#F4600C', color:'#fff', border:'1px solid #F4600C' },
};
